import { Box, Button, HStack, Text, Textarea, VStack } from '@chakra-ui/react';
import Editor from '@monaco-editor/react';
import { useState } from 'react';

interface JsonEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  height?: string;
  disabled?: boolean;
}

export function JsonEditor({
  value,
  onChange,
  placeholder = 'Enter JSON...',
  height = '400px',
  disabled = false,
}: JsonEditorProps) {
  const [mode, setMode] = useState<'text' | 'json'>('text');
  const [jsonError, setJsonError] = useState<string | null>(null);

  const handleTextChange = (newValue: string) => {
    onChange(newValue);
    setJsonError(null);
  };

  const handleJsonChange = (newValue: string | undefined) => {
    if (newValue === undefined) return;

    try {
      // Validate JSON
      JSON.parse(newValue);
      setJsonError(null);
      onChange(newValue);
    } catch (error) {
      setJsonError(error instanceof Error ? error.message : 'Invalid JSON');
      // Still update the value so user can see their changes
      onChange(newValue);
    }
  };

  const formatJson = () => {
    try {
      const parsed = JSON.parse(value);
      const formatted = JSON.stringify(parsed, null, 2);
      onChange(formatted);
      setJsonError(null);
    } catch (error) {
      setJsonError(error instanceof Error ? error.message : 'Invalid JSON');
    }
  };

  const validateJson = () => {
    try {
      JSON.parse(value);
      setJsonError(null);
      return true;
    } catch (error) {
      setJsonError(error instanceof Error ? error.message : 'Invalid JSON');
      return false;
    }
  };

  return (
    <VStack gap={2} align="stretch">
      {/* Mode Toggle */}
      <HStack justify="space-between" align="center">
        <HStack gap={2}>
          <Button
            size="sm"
            variant={mode === 'text' ? 'solid' : 'outline'}
            colorScheme={mode === 'text' ? 'blue' : 'gray'}
            onClick={() => setMode('text')}
            disabled={disabled}
          >
            Text
          </Button>
          <Button
            size="sm"
            variant={mode === 'json' ? 'solid' : 'outline'}
            colorScheme={mode === 'json' ? 'blue' : 'gray'}
            onClick={() => setMode('json')}
            disabled={disabled}
          >
            JSON
          </Button>
        </HStack>

        {mode === 'json' && (
          <HStack gap={2}>
            <Button
              size="sm"
              variant="outline"
              onClick={formatJson}
              disabled={disabled || !value.trim()}
            >
              Format
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={validateJson}
              disabled={disabled || !value.trim()}
            >
              Validate
            </Button>
          </HStack>
        )}
      </HStack>

      {/* Error Display */}
      {jsonError && (
        <Box
          bg="red.50"
          border="1px solid"
          borderColor="red.200"
          borderRadius="md"
          p={2}
        >
          <Text fontSize="sm" color="red.600">
            JSON Error: {jsonError}
          </Text>
        </Box>
      )}

      {/* Editor */}
      <Box
        border="1px solid"
        borderColor={jsonError ? 'red.300' : 'gray.200'}
        borderRadius="md"
        overflow="hidden"
      >
        {mode === 'text' ? (
          <Textarea
            value={value}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              handleTextChange(e.target.value)
            }
            placeholder={placeholder}
            height={height}
            width="100%"
            p={3}
            fontSize="sm"
            fontFamily="mono"
            bg="white"
            border="none"
            outline="none"
            resize="none"
            disabled={disabled}
            _disabled={{
              bg: 'gray.50',
              cursor: 'not-allowed',
            }}
            _placeholder={{
              color: 'gray.500',
            }}
          />
        ) : (
          <Editor
            height={height}
            defaultLanguage="json"
            value={value}
            onChange={handleJsonChange}
            options={{
              readOnly: disabled,
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              wordWrap: 'on',
              lineNumbers: 'on',
              folding: true,
              automaticLayout: true,
              formatOnPaste: true,
              formatOnType: true,
              tabSize: 2,
              insertSpaces: true,
            }}
            theme="vs-light"
          />
        )}
      </Box>

      {/* Help Text */}
      {mode === 'json' && (
        <Text fontSize="xs" color="gray.500">
          JSON mode provides syntax highlighting, validation, and formatting.
          Use the Format button to prettify your JSON.
        </Text>
      )}
    </VStack>
  );
}
